import type { Session, User } from '@supabase/supabase-js';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Profile } from '../types';
import { getSupabase, isSupabaseConfigured, supabase } from '../lib/supabase';

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  authMessage: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (input: {
    email: string;
    password: string;
    username: string;
    displayName: string;
  }) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  updateProfile: (updates: Partial<Pick<Profile, 'display_name' | 'username' | 'bio' | 'avatar_url'>>) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

async function fetchProfile(userId: string) {
  const client = getSupabase();
  const { data, error } = await client.from('profiles').select('*').eq('id', userId).maybeSingle();

  if (error) throw error;
  return data;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [authMessage, setAuthMessage] = useState<string | null>(null);

  const user = session?.user ?? null;

  const loadProfile = useCallback(async (nextUser: User | null) => {
    if (!nextUser || !isSupabaseConfigured) {
      setProfile(null);
      return;
    }

    const existingProfile = await fetchProfile(nextUser.id);
    setProfile(existingProfile);
  }, []);

  useEffect(() => {
    if (!isSupabaseConfigured || !supabase) {
      setLoading(false);
      return;
    }

    let isMounted = true;

    supabase.auth.getSession().then(async ({ data, error }) => {
      if (!isMounted) return;

      if (error) {
        setAuthMessage(error.message);
        setLoading(false);
        return;
      }

      setSession(data.session);

      try {
        await loadProfile(data.session?.user ?? null);
      } catch (profileError) {
        setAuthMessage(profileError instanceof Error ? profileError.message : 'Could not load profile.');
      } finally {
        setLoading(false);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      loadProfile(nextSession?.user ?? null).catch((profileError) => {
        setAuthMessage(profileError instanceof Error ? profileError.message : 'Could not refresh profile.');
      });
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [loadProfile]);

  const signIn = useCallback(async (email: string, password: string) => {
    const client = getSupabase();
    setAuthMessage(null);

    const { error } = await client.auth.signInWithPassword({ email, password });
    if (error) throw error;
  }, []);

  const signUp = useCallback(
    async ({
      email,
      password,
      username,
      displayName,
    }: {
      email: string;
      password: string;
      username: string;
      displayName: string;
    }) => {
      const client = getSupabase();
      setAuthMessage(null);

      const { data, error } = await client.auth.signUp({
        email,
        password,
        options: {
          data: {
            username,
            display_name: displayName,
          },
        },
      });

      if (error) throw error;

      if (!data.session) {
        setAuthMessage('Check your email to confirm your account, then come back to sign in.');
      }
    },
    [],
  );

  const signOut = useCallback(async () => {
    const client = getSupabase();
    await client.auth.signOut();
    setProfile(null);
  }, []);

  const refreshProfile = useCallback(async () => {
    await loadProfile(user);
  }, [loadProfile, user]);

  const updateProfile = useCallback(
    async (updates: Partial<Pick<Profile, 'display_name' | 'username' | 'bio' | 'avatar_url'>>) => {
      if (!user) return;

      const client = getSupabase();
      const { error } = await client
        .from('profiles')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', user.id);

      if (error) throw error;
      await refreshProfile();
    },
    [refreshProfile, user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      session,
      profile,
      loading,
      authMessage,
      signIn,
      signUp,
      signOut,
      refreshProfile,
      updateProfile,
    }),
    [authMessage, loading, profile, refreshProfile, session, signIn, signOut, signUp, updateProfile, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider.');
  return context;
}
